// NotificationCenter.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faBell, 
  faCheckCircle, 
  faExclamationTriangle, 
  faInfoCircle, 
  faTimesCircle,
  faSyncAlt,
  faServer,
  faUser,
  faPaperclip,
  faEye,
  faEyeSlash,
  faTrashAlt,
  faCheck,
  faCheckDouble,
  faStar,
  faCircle,
  faChevronLeft,
  faChevronRight,
  faUsers,
  faUserTie,
  faUserCog,
  faChartLine,
  faBriefcase,
  faHeadset,
  faComment,
  faComments,
  faBuilding,
  faPaperPlane,
  faSpinner,
  faTimes as faTimesIcon,
  faBug
} from '@fortawesome/free-solid-svg-icons';
// import '../styles/NotificationsCenter.css';
import logo from '../assets/images/logo.png';

// Sender type configuration - Updated with new profiles
const SENDER_TYPES = {
  'admin': { 
    icon: faUserTie, 
    label: 'Admin',
    color: '#4a9eff'
  },
  'manager': { 
    icon: faUserCog, 
    label: 'Manager',
    color: '#ff9500'
  },
  'sales_executive': { 
    icon: faChartLine, 
    label: 'Sales Executive',
    color: '#25d366'
  },
  'finance_officer': { 
    icon: faBriefcase, 
    label: 'Finance Officer',
    color: '#6366f1'
  },
  'technical_team': { 
    icon: faHeadset, 
    label: 'Technical Team',
    color: '#ff3b30'
  },
  'general_management': { 
    icon: faUserTie, 
    label: 'General Management',
    color: '#4a9eff'
  },
  'finance': { 
    icon: faBriefcase, 
    label: 'Finance',
    color: '#6366f1'
  },
  'support': { 
    icon: faComments, 
    label: 'Support',
    color: '#25d366'
  },
  'system': { 
    icon: faServer, 
    label: 'System',
    color: '#667781'
  }
};

// Profile icons mapping for quick access
const PROFILE_ICONS = {
  'general_management': { icon: faUserTie, color: '#4a9eff', label: 'General Management' },
  'technical_team': { icon: faHeadset, color: '#ff3b30', label: 'Technical Team' },
  'finance': { icon: faBriefcase, color: '#6366f1', label: 'Finance' },
  'support': { icon: faComments, color: '#25d366', label: 'Support' }
};

// Helper function to get icon for message type
const getTypeIcon = (type) => {
  switch (type) {
    case 'success': return faCheckCircle;
    case 'warning': return faExclamationTriangle;
    case 'error': return faTimesCircle;
    case 'system': return faServer;
    default: return faInfoCircle;
  }
};

const NotificationCenter = ({ onClose, initialNotificationId, initialSender }) => {
  const [notifications, setNotifications] = useState([]);
  const [senders, setSenders] = useState([]);
  const [selectedSender, setSelectedSender] = useState(null);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showSidebar, setShowSidebar] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [markingAll, setMarkingAll] = useState(false);
  const [user, setUser] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const messagesEndRef = useRef(null);
  const [authToken, setAuthToken] = useState(null);
  const [showSendMessage, setShowSendMessage] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [debugData, setDebugData] = useState(null);
  const [authError, setAuthError] = useState(null);

  // Get user from auth context or localStorage - FIXED to properly get role
  useEffect(() => {
    const getUserFromStorage = () => {
      try {
        // Try to get user from localStorage
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          const parsedUser = JSON.parse(storedUser);
          
          // IMPORTANT: Also get the role from user_role in localStorage
          const storedRole = localStorage.getItem('user_role');
          if (storedRole) {
            parsedUser.role = storedRole;
          }
          
          // Get current shop info
          const storedShop = localStorage.getItem('current_shop');
          if (storedShop) {
            try {
              const shopData = JSON.parse(storedShop);
              parsedUser.shop_id = shopData.shop_id;
              parsedUser.shop_name = shopData.shop_name;
              parsedUser.current_shop_role = shopData.shop_role || parsedUser.role;
            } catch (e) {
              console.error('Error parsing shop data:', e);
            }
          }
          
          console.log('NotificationCenter - User loaded:', parsedUser);
          setUser(parsedUser);
          
          // Generate auth token with proper role
          const authData = {
            user_id: parsedUser.id || parsedUser.user_id || 1,
            username: parsedUser.username || 'admin',
            role: parsedUser.role || 'super_admin',
            fullname: parsedUser.fullname || parsedUser.name || 'User'
          };
          
          const token = btoa(JSON.stringify(authData));
          console.log('NotificationCenter - Auth token generated:', authData);
          setAuthToken(token);
          
          return parsedUser;
        }
        return null;
      } catch (error) {
        console.error('Error getting user from storage:', error);
        setAuthError('Failed to load user data');
        return null;
      }
    };

    const userData = getUserFromStorage();
    
    // If no user found, use default for testing
    if (!userData) {
      console.warn('No user found in localStorage, using default');
      const defaultUser = {
        id: 1,
        username: 'admin',
        role: 'super_admin',
        fullname: 'Administrator'
      };
      setUser(defaultUser);
      const authData = {
        user_id: 1,
        username: 'admin',
        role: 'super_admin',
        fullname: 'Administrator'
      };
      setAuthToken(btoa(JSON.stringify(authData)));
    }
  }, []);

  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setShowSidebar(true);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch senders - FIXED to handle auth properly
  const fetchSenders = useCallback(async () => {
    if (!user?.username) {
      console.warn('No username available for fetchSenders');
      setLoading(false);
      return;
    }

    if (!authToken) {
      console.warn('No auth token available for fetchSenders');
      setLoading(false);
      return;
    }

    try {
      setRefreshing(true);
      console.log('Fetching senders for user:', user.username, 'role:', user.role);
      
      const url = `https://ourzestapp.online/oz_backend/notifications.php?action=senders`;
      console.log('Fetch URL:', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      console.log('Senders response:', data);
      
      if (data.success && data.senders) {
        let senderList = data.senders;
        
        // Ensure chat profiles are always included even if they have no messages
        const chatProfileNames = ['general_management', 'technical_team', 'finance', 'support'];
        const existingNames = new Set(senderList.map(s => s.sender_name));
        
        chatProfileNames.forEach(profileName => {
          if (!existingNames.has(profileName)) {
            const profileInfo = PROFILE_ICONS[profileName];
            if (profileInfo) {
              senderList.push({
                sender_name: profileName,
                sender_role: profileInfo.label || profileName,
                sender_fullname: profileInfo.label || profileName,
                notification_count: 0,
                unread_count: 0,
                last_message_date: null,
                is_chat_profile: true,
                profile_icon: profileInfo.icon,
                profile_color: profileInfo.color,
                display_name: profileInfo.label || profileName
              });
            }
          }
        });
        
        setSenders(senderList);
        
        // Select a sender
        if (senderList.length > 0) {
          // Try to find the sender with unread messages first
          let senderToSelect = senderList.find(s => s.unread_count > 0);
          
          // If no unread, select the first one
          if (!senderToSelect) {
            senderToSelect = senderList[0];
          }
          
          setSelectedSender(senderToSelect);
          await fetchNotificationsBySender(senderToSelect.sender_name);
        } else {
          setLoading(false);
        }
      } else {
        console.warn('No senders returned or success false:', data);
        setLoading(false);
      }
    } catch (error) {
      console.error('Error fetching senders:', error);
      setAuthError('Failed to fetch senders: ' + error.message);
      setLoading(false);
    } finally {
      setRefreshing(false);
    }
  }, [user, authToken]);

  // Fetch notifications by sender - FIXED to handle auth properly
  const fetchNotificationsBySender = useCallback(async (senderName) => {
    if (!user?.username) {
      console.warn('No username for fetchNotificationsBySender');
      setLoading(false);
      return;
    }

    if (!authToken) {
      console.warn('No auth token for fetchNotificationsBySender');
      setLoading(false);
      return;
    }

    if (!senderName) {
      console.warn('No senderName for fetchNotificationsBySender');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log('Fetching notifications for sender:', senderName, 'user:', user.username);
      
      const url = `https://ourzestapp.online/oz_backend/notifications.php?action=list&limit=50&sender=${encodeURIComponent(senderName)}`;
      console.log('Fetch URL:', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      console.log('Notifications response for', senderName, ':', data);
      
      if (data.success) {
        const sortedNotifications = (data.notifications || []).sort((a, b) => {
          if (a.is_read !== b.is_read) return a.is_read ? 1 : -1;
          return new Date(b.created_at) - new Date(a.created_at);
        });
        setNotifications(sortedNotifications);
        setUnreadCount(data.unread_count || 0);
        
        if (sortedNotifications.length > 0) {
          const firstUnread = sortedNotifications.find(n => !n.is_read);
          setSelectedNotification(firstUnread || sortedNotifications[0]);
        } else {
          setSelectedNotification(null);
        }
      } else {
        console.warn('No notifications returned for sender:', senderName, data);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setAuthError('Failed to fetch notifications: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [user, authToken]);

  // Initial fetch
  useEffect(() => {
    if (user && authToken) {
      fetchSenders();
    }
  }, [user, authToken]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [selectedNotification, notifications]);

  // Mark a notification as read
  const markAsRead = async (id) => {
    if (!id || !authToken) return;

    try {
      const response = await fetch('https://ourzestapp.online/oz_backend/notifications.php?action=mark_read', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: id })
      });

      const data = await response.json();
      if (data.success) {
        setNotifications(prev =>
          prev.map(notif =>
            notif.id === id ? { ...notif, is_read: true } : notif
          )
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
        if (selectedNotification && selectedNotification.id === id) {
          setSelectedNotification(prev => ({ ...prev, is_read: true }));
        }
        // Refresh senders to update counts
        fetchSenders();
      }
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  // Mark all as read for current sender
  const markAllAsRead = async () => {
    if (unreadCount === 0 || !authToken) return;

    setMarkingAll(true);
    try {
      const response = await fetch('https://ourzestapp.online/oz_backend/notifications.php?action=mark_all_read', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({})
      });

      const data = await response.json();
      if (data.success) {
        setNotifications(prev =>
          prev.map(notif => ({ ...notif, is_read: true }))
        );
        setUnreadCount(0);
        if (selectedNotification) {
          setSelectedNotification(prev => ({ ...prev, is_read: true }));
        }
        fetchSenders();
      }
    } catch (error) {
      console.error('Error marking all as read:', error);
    } finally {
      setMarkingAll(false);
    }
  };

  // Delete all messages from a sender
  const deleteAllFromSender = async (senderName) => {
    if (!senderName || !authToken) return;
    if (!window.confirm(`Delete all messages from ${senderName}?`)) return;

    try {
      const response = await fetch('https://ourzestapp.online/oz_backend/notifications.php?action=delete_all', {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sender: senderName })
      });

      const data = await response.json();
      if (data.success) {
        setNotifications([]);
        setSelectedNotification(null);
        fetchSenders();
      }
    } catch (error) {
      console.error('Error deleting all messages:', error);
    }
  };

  // Get notification icon
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'success': return faCheckCircle;
      case 'warning': return faExclamationTriangle;
      case 'error': return faTimesCircle;
      case 'system': return faServer;
      default: return faInfoCircle;
    }
  };

  // Get notification type class
  const getNotificationTypeClass = (type) => {
    switch (type) {
      case 'success': return 'success';
      case 'warning': return 'warning';
      case 'error': return 'error';
      case 'system': return 'system';
      default: return 'info';
    }
  };

  // Format date for chat display
  const formatChatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      if (date >= today) {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      } else if (date >= yesterday) {
        return 'Yesterday';
      } else {
        return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
      }
    } catch (e) {
      return dateString;
    }
  };

  // Get full date for tooltip
  const getFullDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleString([], {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  };

  // Filter notifications by type
  const filteredNotifications = notifications.filter(notif => {
    if (filterType !== 'all' && notif.notification_type !== filterType) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (notif.title || '').toLowerCase().includes(query) ||
             (notif.message || '').toLowerCase().includes(query);
    }
    return true;
  });

  // Delete notification
  const deleteNotification = async (id) => {
    if (!id || !authToken) return;
    if (!window.confirm('Delete this notification?')) return;

    try {
      const response = await fetch('https://ourzestapp.online/oz_backend/notifications.php', {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: id })
      });

      const data = await response.json();
      if (data.success) {
        setNotifications(prev => prev.filter(n => n.id !== id));
        if (selectedNotification && selectedNotification.id === id) {
          const remaining = filteredNotifications.filter(n => n.id !== id);
          setSelectedNotification(remaining[0] || null);
        }
        fetchSenders();
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  // Get sender avatar/icon - Updated with new profiles
  const getSenderIcon = (senderName) => {
    const lowerName = senderName?.toLowerCase() || '';
    
    // Check for new chat profiles first
    if (lowerName.includes('general') || lowerName.includes('management')) {
      return SENDER_TYPES.general_management;
    }
    if (lowerName.includes('technical') || lowerName.includes('tech')) {
      return SENDER_TYPES.technical_team;
    }
    if (lowerName.includes('finance')) {
      return SENDER_TYPES.finance;
    }
    if (lowerName.includes('support')) {
      return SENDER_TYPES.support;
    }
    
    // Check for existing sender types
    if (lowerName.includes('admin')) return SENDER_TYPES.admin;
    if (lowerName.includes('manager')) return SENDER_TYPES.manager;
    if (lowerName.includes('sales') || lowerName.includes('executive')) return SENDER_TYPES.sales_executive;
    
    return SENDER_TYPES.system;
  };

  // Get profile info for a sender
  const getProfileInfo = (senderName) => {
    const lowerName = senderName?.toLowerCase() || '';
    if (lowerName.includes('general') || lowerName.includes('management')) {
      return PROFILE_ICONS.general_management;
    }
    if (lowerName.includes('technical') || lowerName.includes('tech')) {
      return PROFILE_ICONS.technical_team;
    }
    if (lowerName.includes('finance')) {
      return PROFILE_ICONS.finance;
    }
    if (lowerName.includes('support')) {
      return PROFILE_ICONS.support;
    }
    return null;
  };

  // Filter types with icons
  const filterTypes = [
    { key: 'all', label: 'All', icon: faBell },
    { key: 'info', label: 'Info', icon: faInfoCircle },
    { key: 'success', label: 'Success', icon: faCheckCircle },
    { key: 'warning', label: 'Warning', icon: faExclamationTriangle },
    { key: 'error', label: 'Error', icon: faTimesCircle },
    { key: 'system', label: 'System', icon: faServer },
  ];

  // Handle sender selection
  const handleSenderSelect = (sender) => {
    setSelectedSender(sender);
    fetchNotificationsBySender(sender.sender_name);
    if (isMobile) {
      setShowSidebar(false);
    }
  };

  // Check if user can send messages
  const canSendMessages = () => {
    const role = user?.role || '';
    // Allow sending for admin and manager roles
    return role === 'super_admin' || role === 'admin' || 
           role === 'admin_mechanicsap' || role === 'manager_mechanicsap' ||
           role === 'admin_cosmetics' || role === 'manager_cosmetics';
  };

  // Handle refresh
  const handleRefresh = () => {
    setSenders([]);
    setNotifications([]);
    fetchSenders();
  };

  // Handle debug
  const handleDebug = async () => {
    if (!authToken) return;
    try {
      const response = await fetch(`https://ourzestapp.online/oz_backend/notifications.php?action=debug`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      console.log('Debug data:', data);
      setDebugData(data);
      alert('Debug data logged to console. Check the console for details.');
    } catch (error) {
      console.error('Debug error:', error);
      alert('Error fetching debug data');
    }
  };

  // Loading state
  if (loading && senders.length === 0) {
    return (
      <div className="notification-center-loading">
        <div className="loading-spinner"></div>
        <p>Loading conversations...</p>
      </div>
    );
  }

  return (
    <div className="notification-center">
      {/* Header */}
      <div className="notification-center-header">
        <div className="header-left">
          <button className="back-button" onClick={onClose}>
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <div className="header-title">
            <img src={logo} alt="OurZest" className="header-logo" />
            <h2>Messages</h2>
            {unreadCount > 0 && (
              <span className="unread-badge">{unreadCount} new</span>
            )}
          </div>
        </div>
        <div className="header-right">
          <button 
            className="debug-btn" 
            onClick={handleDebug}
            title="Debug"
            style={{
              background: 'none',
              border: 'none',
              color: '#6b7280',
              cursor: 'pointer',
              padding: '8px',
              fontSize: '16px'
            }}
          >
            <FontAwesomeIcon icon={faBug} />
          </button>
          {canSendMessages() && (
            <button 
              className="send-message-btn"
              onClick={() => setShowSendMessage(true)}
              title="Send Message"
            >
              <FontAwesomeIcon icon={faPaperPlane} />
              <span>Send</span>
            </button>
          )}
          {unreadCount > 0 && (
            <button 
              className="mark-all-read-btn" 
              onClick={markAllAsRead}
              disabled={markingAll}
            >
              <FontAwesomeIcon icon={faCheckDouble} />
              <span>Mark all read</span>
            </button>
          )}
          <button 
            className="refresh-btn" 
            onClick={handleRefresh}
            disabled={refreshing}
            title="Refresh"
          >
            <FontAwesomeIcon icon={faSyncAlt} spin={refreshing} />
          </button>
        </div>
      </div>

      {/* Debug error display */}
      {authError && (
        <div className="auth-error-banner" style={{
          background: '#fee2e2',
          color: '#dc2626',
          padding: '8px 16px',
          margin: '0 16px',
          borderRadius: '8px',
          fontSize: '14px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span>{authError}</span>
          <button 
            onClick={() => setAuthError(null)}
            style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer' }}
          >
            <FontAwesomeIcon icon={faTimesIcon} />
          </button>
        </div>
      )}

      <div className="notification-center-body">
        {/* Sidebar - Sender List */}
        <div className={`notification-sidebar ${!showSidebar ? 'hidden' : ''}`}>
          <div className="sidebar-search">
            <input
              type="text"
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="sidebar-senders">
            {senders.length === 0 ? (
              <div className="empty-state">
                <FontAwesomeIcon icon={faComments} className="empty-icon" />
                <p>No conversations yet</p>
                <p className="empty-sub">Messages from teams will appear here</p>
              </div>
            ) : (
              senders.map((sender) => {
                const senderType = getSenderIcon(sender.sender_name);
                const profileInfo = getProfileInfo(sender.sender_name);
                const isActive = selectedSender?.sender_name === sender.sender_name;
                const hasUnread = (sender.unread_count || 0) > 0;
                const isChatProfile = sender.is_chat_profile || profileInfo !== null;
                
                return (
                  <div
                    key={sender.sender_name}
                    className={`sender-item ${isActive ? 'active' : ''} ${hasUnread ? 'has-unread' : ''} ${isChatProfile ? 'chat-profile' : ''}`}
                    onClick={() => handleSenderSelect(sender)}
                  >
                    <div className="sender-avatar" style={{ 
                      background: profileInfo?.color || senderType.color 
                    }}>
                      <FontAwesomeIcon icon={profileInfo?.icon || senderType.icon} />
                    </div>
                    <div className="sender-info">
                      <div className="sender-name-row">
                        <span className="sender-name">
                          {profileInfo?.label || sender.display_name || sender.sender_name}
                          {isChatProfile && (
                            <span className="profile-badge">Team</span>
                          )}
                        </span>
                        {hasUnread && (
                          <span className="unread-count-badge">{sender.unread_count}</span>
                        )}
                      </div>
                      <div className="sender-meta">
                        <span className="sender-role">{sender.sender_role || 'Team'}</span>
                        <span className="sender-time">
                          {sender.last_message_date ? formatChatDate(sender.last_message_date) : ''}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Main Chat Area */}
        <div className={`chat-area ${!showSidebar ? 'full' : ''}`}>
          {selectedSender ? (
            <>
              {/* Chat Header */}
              <div className="chat-header">
                <div className="chat-header-left">
                  {isMobile && (
                    <button 
                      className="back-to-list-btn"
                      onClick={() => setShowSidebar(true)}
                    >
                      <FontAwesomeIcon icon={faChevronLeft} />
                    </button>
                  )}
                  <div className="chat-avatar">
                    {(() => {
                      const senderType = getSenderIcon(selectedSender.sender_name);
                      const profileInfo = getProfileInfo(selectedSender.sender_name);
                      return (
                        <div className="sender-avatar-large" style={{ 
                          background: profileInfo?.color || senderType.color 
                        }}>
                          <FontAwesomeIcon icon={profileInfo?.icon || senderType.icon} />
                        </div>
                      );
                    })()}
                  </div>
                  <div className="chat-header-info">
                    <h3 className="chat-sender-name">
                      {getProfileInfo(selectedSender.sender_name)?.label || selectedSender.display_name || selectedSender.sender_name}
                    </h3>
                    <div className="chat-meta">
                      <span className="sender-role-badge">
                        {selectedSender.sender_role || 'Team Member'}
                      </span>
                      <span className="message-count-badge">
                        {notifications.length} messages
                      </span>
                    </div>
                  </div>
                </div>
                <div className="chat-header-right">
                  {unreadCount > 0 && (
                    <button 
                      className="action-btn mark-read-btn"
                      onClick={markAllAsRead}
                      disabled={markingAll}
                      title="Mark all as read"
                    >
                      <FontAwesomeIcon icon={faCheckDouble} />
                    </button>
                  )}
                  <button 
                    className="action-btn delete-btn"
                    onClick={() => deleteAllFromSender(selectedSender.sender_name)}
                    title="Delete all messages from this sender"
                  >
                    <FontAwesomeIcon icon={faTrashAlt} />
                  </button>
                </div>
              </div>

              {/* Chat Messages */}
              <div className="chat-messages">
                <div className="messages-container">
                  {filteredNotifications.length === 0 ? (
                    <div className="empty-messages">
                      <FontAwesomeIcon icon={faComment} className="empty-msg-icon" />
                      <p>No messages from {getProfileInfo(selectedSender.sender_name)?.label || selectedSender.display_name || selectedSender.sender_name}</p>
                      {searchQuery && <p className="empty-sub">Try adjusting your search</p>}
                    </div>
                  ) : (
                    filteredNotifications.map((notification, index) => {
                      const prevDate = index > 0 ? filteredNotifications[index - 1].created_at : null;
                      const showDateDivider = !prevDate || 
                        new Date(notification.created_at).toDateString() !== new Date(prevDate).toDateString();
                      
                      return (
                        <React.Fragment key={notification.id}>
                          {showDateDivider && (
                            <div className="message-date-divider">
                              <span>{formatChatDate(notification.created_at)}</span>
                            </div>
                          )}
                          <div 
                            className={`message-wrapper received ${!notification.is_read ? 'unread-msg' : ''}`}
                            onClick={() => {
                              if (!notification.is_read) {
                                markAsRead(notification.id);
                              }
                            }}
                          >
                            <div className={`message-bubble received ${notification.is_important ? 'important' : ''}`}>
                              <div className="message-title">{notification.title}</div>
                              <div className="message-text">{notification.message}</div>
                              <div className="message-footer">
                                <span className="message-time" title={getFullDate(notification.created_at)}>
                                  {formatChatDate(notification.created_at)}
                                </span>
                                <span className="message-read-status">
                                  {notification.is_read ? (
                                    <FontAwesomeIcon icon={faCheckDouble} className="read" />
                                  ) : (
                                    <FontAwesomeIcon icon={faCheck} className="delivered" />
                                  )}
                                </span>
                                {notification.is_important && (
                                  <FontAwesomeIcon icon={faStar} className="important-star-msg" />
                                )}
                              </div>
                            </div>
                          </div>
                        </React.Fragment>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </div>
              </div>

              {/* Chat Footer */}
              <div className="chat-footer">
                <div className="footer-info">
                  <span className="footer-label">Conversation with:</span>
                  <span className="footer-value">
                    {getProfileInfo(selectedSender.sender_name)?.label || selectedSender.display_name || selectedSender.sender_name}
                  </span>
                  {selectedSender.sender_role && (
                    <>
                      <span className="footer-separator">•</span>
                      <span className="footer-label">Role:</span>
                      <span className="footer-value">{selectedSender.sender_role}</span>
                    </>
                  )}
                </div>
                <div className="footer-actions">
                  <span className="status-text">
                    {unreadCount > 0 ? `${unreadCount} unread` : 'All read ✓'}
                  </span>
                </div>
              </div>
            </>
          ) : (
            // No sender selected
            <div className="empty-chat-state">
              <div className="empty-chat-icon">
                <FontAwesomeIcon icon={faComments} />
              </div>
              <h3>Select a conversation</h3>
              <p>Choose a team or person from the list to view messages</p>
            </div>
          )}
        </div>
      </div>

      {/* Send Message Modal */}
      {showSendMessage && (
        <SendMessageModal
          onClose={() => setShowSendMessage(false)}
          user={user}
          authToken={authToken}
          onMessageSent={() => {
            handleRefresh();
          }}
        />
      )}
    </div>
  );
};

// ==============================================
// SEND MESSAGE MODAL COMPONENT
// ==============================================

const SendMessageModal = ({ onClose, user, authToken, onMessageSent }) => {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  const [recipientType, setRecipientType] = useState('all');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [senderProfile, setSenderProfile] = useState('');
  const [isImportant, setIsImportant] = useState(false);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [searchUser, setSearchUser] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Available sender profiles
  const senderProfiles = [
    { value: 'general_management', label: 'General Management', icon: faUserTie, color: '#4a9eff' },
    { value: 'technical_team', label: 'Technical Team', icon: faHeadset, color: '#ff3b30' },
    { value: 'finance', label: 'Finance', icon: faBriefcase, color: '#6366f1' },
    { value: 'support', label: 'Support', icon: faComments, color: '#25d366' }
  ];

  // Fetch users and extract roles
  useEffect(() => {
    const fetchUsers = async () => {
      setLoadingUsers(true);
      try {
        const userResponse = await fetch('https://ourzestapp.online/oz_backend/root_users.php?action=list&limit=1000', {
          method: 'GET',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          }
        });
        
        const userData = await userResponse.json();
        console.log('Users response:', userData);
        
        if (userData.success && userData.data) {
          setUsers(userData.data);
          
          const uniqueRoles = [];
          const roleSet = new Set();
          
          userData.data.forEach(user => {
            if (user.role && !roleSet.has(user.role)) {
              roleSet.add(user.role);
              uniqueRoles.push(user.role);
            }
          });
          
          const commonRoles = [
            'super_admin', 'admin', 'admin_mechanicsap', 'manager_mechanicsap',
            'user_mechanicsap', 'viewer_mechanics', 'customer', 
            'supplier', 'supplier_premium', 'supplier_gold', 'supplier_platinum'
          ];
          
          commonRoles.forEach(role => {
            if (!roleSet.has(role)) {
              roleSet.add(role);
              uniqueRoles.push(role);
            }
          });
          
          setRoles(uniqueRoles);
          console.log('Extracted roles:', uniqueRoles);
        } else {
          setRoles([
            'super_admin', 'admin', 'admin_mechanicsap', 'manager_mechanicsap',
            'user_mechanicsap', 'viewer_mechanics', 'customer',
            'supplier', 'supplier_premium', 'supplier_gold', 'supplier_platinum'
          ]);
        }
      } catch (error) {
        console.error('Error fetching users:', error);
        setRoles([
          'super_admin', 'admin', 'admin_mechanicsap', 'manager_mechanicsap',
          'user_mechanicsap', 'viewer_mechanics', 'customer',
          'supplier', 'supplier_premium', 'supplier_gold', 'supplier_platinum'
        ]);
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchUsers();
  }, [authToken]);

  const handleSend = async () => {
    if (!title.trim() || !message.trim()) {
      alert('Please fill in title and message');
      return;
    }

    if (!senderProfile) {
      alert('Please select a sender profile');
      return;
    }

    if (recipientType === 'specific' && selectedUsers.length === 0) {
      alert('Please select at least one user');
      return;
    }

    if (recipientType === 'role_based' && selectedRoles.length === 0) {
      alert('Please select at least one role');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        title: title.trim(),
        message: message.trim(),
        type: messageType,
        recipient_type: recipientType,
        important: isImportant ? 1 : 0,
        sender_name: senderProfile,
        sender_display_name: senderProfiles.find(p => p.value === senderProfile)?.label || senderProfile,
        username: user?.username,
        user_id: user?.id || user?.user_id,
        role: user?.role
      };

      if (recipientType === 'specific') {
        payload.recipients = selectedUsers;
      } else if (recipientType === 'role_based') {
        payload.recipients = selectedRoles;
      }

      console.log('Sending payload:', payload);

      const response = await fetch('https://ourzestapp.online/oz_backend/notifications.php?action=send', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      console.log('Send response:', data);
      
      if (data.success) {
        alert(`Message sent successfully to ${data.sent_count || 0} recipients!`);
        onMessageSent();
        setTimeout(() => {
          onClose();
        }, 500);
      } else {
        alert('Error sending message: ' + (data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Error sending message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(user => 
    user.username?.toLowerCase().includes(searchUser.toLowerCase()) ||
    user.fullname?.toLowerCase().includes(searchUser.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchUser.toLowerCase())
  );

  const toggleUser = (username) => {
    setSelectedUsers(prev => 
      prev.includes(username) 
        ? prev.filter(u => u !== username)
        : [...prev, username]
    );
  };

  const toggleRole = (role) => {
    setSelectedRoles(prev => 
      prev.includes(role) 
        ? prev.filter(r => r !== role)
        : [...prev, role]
    );
  };

  const getRoleDisplayName = (role) => {
    return role.replace(/_/g, ' ').toUpperCase();
  };

  return (
    <div className="send-message-overlay" onClick={onClose}>
      <div className="send-message-modal" onClick={(e) => e.stopPropagation()}>
        <div className="send-message-header">
          <h3>
            <FontAwesomeIcon icon={faPaperPlane} />
            Send Message
          </h3>
          <button className="close-modal-btn" onClick={onClose}>
            <FontAwesomeIcon icon={faTimesIcon} />
          </button>
        </div>

        <div className="send-message-body">
          <div className="form-group">
            <label>Send As:</label>
            <div className="profile-selector">
              {senderProfiles.map(profile => (
                <button
                  key={profile.value}
                  className={`profile-option ${senderProfile === profile.value ? 'active' : ''}`}
                  onClick={() => setSenderProfile(profile.value)}
                  style={{
                    borderColor: senderProfile === profile.value ? profile.color : '#ddd'
                  }}
                >
                  <FontAwesomeIcon icon={profile.icon} style={{ color: profile.color }} />
                  <span>{profile.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>Title:</label>
            <input
              type="text"
              placeholder="Message title..."
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={255}
            />
          </div>

          <div className="form-group">
            <label>Message:</label>
            <textarea
              placeholder="Type your message here..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
            />
          </div>

          <div className="form-group">
            <label>Message Type:</label>
            <div className="type-selector">
              {['info', 'success', 'warning', 'error', 'system'].map(type => (
                <button
                  key={type}
                  className={`type-option ${messageType === type ? 'active' : ''}`}
                  onClick={() => setMessageType(type)}
                >
                  <FontAwesomeIcon icon={getTypeIcon(type)} />
                  <span>{type.charAt(0).toUpperCase() + type.slice(1)}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>Send To:</label>
            <div className="recipient-type-selector">
              <button
                className={`recipient-option ${recipientType === 'all' ? 'active' : ''}`}
                onClick={() => setRecipientType('all')}
              >
                <FontAwesomeIcon icon={faUsers} />
                <span>All Users</span>
              </button>
              <button
                className={`recipient-option ${recipientType === 'specific' ? 'active' : ''}`}
                onClick={() => setRecipientType('specific')}
              >
                <FontAwesomeIcon icon={faUser} />
                <span>Specific Users</span>
              </button>
              <button
                className={`recipient-option ${recipientType === 'role_based' ? 'active' : ''}`}
                onClick={() => setRecipientType('role_based')}
              >
                <FontAwesomeIcon icon={faUserCog} />
                <span>By Role</span>
              </button>
            </div>
          </div>

          {recipientType === 'specific' && (
            <div className="form-group">
              <label>Select Users:</label>
              <input
                type="text"
                placeholder="Search users..."
                value={searchUser}
                onChange={(e) => setSearchUser(e.target.value)}
                className="user-search-input"
              />
              <div className="user-list">
                {loadingUsers ? (
                  <p className="loading-text">Loading users...</p>
                ) : filteredUsers.length === 0 ? (
                  <p className="no-users">No users found</p>
                ) : (
                  filteredUsers.map(user => (
                    <div
                      key={user.username}
                      className={`user-item ${selectedUsers.includes(user.username) ? 'selected' : ''}`}
                      onClick={() => toggleUser(user.username)}
                    >
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(user.username)}
                        onChange={() => {}}
                      />
                      <span>{user.fullname || user.username}</span>
                      <span className="user-role-badge">{user.role || 'No Role'}</span>
                    </div>
                  ))
                )}
              </div>
              {selectedUsers.length > 0 && (
                <div className="selected-count">
                  {selectedUsers.length} user{selectedUsers.length > 1 ? 's' : ''} selected
                </div>
              )}
            </div>
          )}

          {recipientType === 'role_based' && (
            <div className="form-group">
              <label>Select Roles:</label>
              <div className="role-list">
                {loadingUsers ? (
                  <p className="loading-text">Loading roles...</p>
                ) : roles.length === 0 ? (
                  <p className="no-roles">No roles available</p>
                ) : (
                  roles.map(role => (
                    <div
                      key={role}
                      className={`role-item ${selectedRoles.includes(role) ? 'selected' : ''}`}
                      onClick={() => toggleRole(role)}
                    >
                      <input
                        type="checkbox"
                        checked={selectedRoles.includes(role)}
                        onChange={() => {}}
                      />
                      <span>{getRoleDisplayName(role)}</span>
                    </div>
                  ))
                )}
              </div>
              {selectedRoles.length > 0 && (
                <div className="selected-count">
                  {selectedRoles.length} role{selectedRoles.length > 1 ? 's' : ''} selected
                </div>
              )}
            </div>
          )}

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={isImportant}
                onChange={(e) => setIsImportant(e.target.checked)}
              />
              <FontAwesomeIcon icon={faStar} style={{ color: isImportant ? '#f59e0b' : '#ccc' }} />
              Mark as Important
            </label>
          </div>
        </div>

        <div className="send-message-footer">
          <button className="cancel-btn" onClick={onClose}>
            Cancel
          </button>
          <button 
            className="send-btn" 
            onClick={handleSend}
            disabled={loading || !senderProfile || !title.trim() || !message.trim()}
          >
            {loading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin />
                Sending...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faPaperPlane} />
                Send Message
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationCenter;